import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest, buildApiUrl, downloadApiFile } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import '../admin/quotes/quote-generator.css';

type InspectionItem = {
    itemId: string;
    label: string;
    category: string;
    testType?: string;
    result: 'pass' | 'fail' | 'deficient' | 'n/a';
    note?: string;
    photoUrl?: string;
};

type Inspection = {
    _id: string;
    siteAddress?: string;
    status: 'in_progress' | 'completed';
    items: InspectionItem[];
    clientId?: { firstName?: string; lastName?: string; buildingName?: string; email?: string; phone?: string } | null;
    technicianId?: { name?: string } | null;
    createdAt?: string;
    /** Device-registry workflow */
    deviceDriven?: boolean;
    isNewBuilding?: boolean;
    devicesRegisteredDuringVisit?: number;
};

export default function InspectionSummary(): React.JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const role = ((user as any)?.role || '') as string;
    const canManage = role === 'admin' || role === 'manager';
    const basePath = canManage ? '/admin/inspections' : '/technician/inspections';
    const listPath = (location.state as { backPath?: string } | null)?.backPath ?? basePath;

    const [inspection, setInspection] = useState<Inspection | null>(null);
    const [summary, setSummary] = useState<{ total: number; passed: number; failed: number; deficient: number; na: number } | null>(null);
    const [deficiencies, setDeficiencies] = useState<InspectionItem[]>([]);
    const [trackedDeficiencies, setTrackedDeficiencies] = useState<
        Array<{
            _id: string;
            description: string;
            severity: 'critical' | 'major' | 'minor';
            status: string;
            photos: string[];
            linkedQuoteId?: unknown;
        }>
    >([]);
    const [generating, setGenerating] = useState(false);
    const [defQuoteBusy, setDefQuoteBusy] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadTrackedDeficiencies = useCallback(() => {
        if (!id) return Promise.resolve();
        return apiRequest<{
            success: boolean;
            data: Array<{
                _id: string;
                description: string;
                severity: 'critical' | 'major' | 'minor';
                status: string;
                photos: string[];
                linkedQuoteId?: unknown;
            }>;
        }>(`/api/deficiencies?inspectionId=${encodeURIComponent(id)}`)
            .then((res) => setTrackedDeficiencies(res.data || []))
            .catch(() => setTrackedDeficiencies([]));
    }, [id]);

    const load = useCallback(() => {
        if (!id) return;
        const run = async () => {
            if (canManage) {
                await apiRequest<{ success: boolean }>(`/api/inspections/${id}/sync-deficiencies`, { method: 'POST' }).catch(() => null);
            }
            try {
                const res = await apiRequest<{ success: boolean; data: { inspection: Inspection; summary: { total: number; passed: number; failed: number; deficient: number; na: number }; deficiencies: InspectionItem[] } }>(`/api/inspections/${id}/report`);
                setInspection(res.data.inspection);
                setSummary(res.data.summary);
                setDeficiencies(res.data.deficiencies);
                await loadTrackedDeficiencies();
            } catch (err: unknown) {
                setErrorMessage(err instanceof Error ? err.message : 'Failed to load inspection.');
            }
        };
        void run();
    }, [id, canManage, loadTrackedDeficiencies]);

    useEffect(() => { load(); }, [load]);

    async function createQuoteFromDeficiency(deficiencyId: string) {
        setDefQuoteBusy(deficiencyId);
        setErrorMessage(null);
        try {
            const res = await apiRequest<{ success: boolean; data: { quoteId: string } }>(
                `/api/deficiencies/${deficiencyId}/convert-to-quote`,
                { method: 'POST' }
            );
            navigate(`/admin/quotes/new?id=${encodeURIComponent(res.data.quoteId)}`);
        } catch (err: unknown) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to create quote');
        } finally {
            setDefQuoteBusy(null);
        }
    }

    async function generateQuote() {
        setGenerating(true);
        setErrorMessage(null);
        try {
            const res = await apiRequest<{ success: boolean; data: { quoteId: string } }>(`/api/inspections/${id}/generate-quote`, {
                method: 'POST'
            });
            navigate(`/admin/quotes/new?id=${res.data.quoteId}`);
        } catch (err: unknown) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to generate quote.');
            setGenerating(false);
        }
    }
    async function downloadInspectionPdf() {
        if (!id || inspection?.status !== 'completed') return;
        setPdfBusy(true);
        setErrorMessage(null);
        try {
            await downloadApiFile(`/api/inspections/${id}/pdf`, `inspection-report-${id}.pdf`);
        } catch (e: unknown) {
            setErrorMessage(e instanceof Error ? e.message : 'PDF download failed.');
        } finally {
            setPdfBusy(false);
        }
    }

    async function downloadReport() {
        if (!inspection) return;
        setDownloading(true);
        try {
            const payload = { inspection, summary, deficiencies };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `inspection-report-${inspection._id}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(false);
        }
    }

    if (!inspection) {
        return (
            <AppShell>
                <div className="quote-page">
                    <div className="quote-container">
                        {errorMessage ? (
                            <div className="banner banner-error">{errorMessage}</div>
                        ) : (
                            <p style={{ color: 'var(--dash-muted)' }}>Loading…</p>
                        )}
                    </div>
                </div>
            </AppShell>
        );
    }

    const passed = summary?.passed ?? 0;
    const failed = summary?.failed ?? 0;
    const deficient = summary?.deficient ?? 0;

    const categories = Array.from(new Set(inspection.items.map((i) => i.category)));
    const deviceCategories = categories;

    const clientName = inspection.clientId
        ? `${inspection.clientId.firstName || ''} ${inspection.clientId.lastName || ''}`.trim() || inspection.clientId.buildingName || ''
        : '';

    function resultBadge(result: string) {
        const map: Record<string, { bg: string; color: string; label: string }> = {
            pass: { bg: 'rgba(46,138,87,0.1)', color: '#2e8a57', label: 'Pass' },
            fail: { bg: 'rgba(192,57,43,0.1)', color: '#c0392b', label: 'Fail' },
            deficient: { bg: 'rgba(230,168,23,0.12)', color: '#b8860b', label: 'Deficient' },
            'n/a': { bg: 'rgba(148,163,184,0.14)', color: '#475569', label: 'N/A' },
            '—': { bg: '#f1f5f9', color: '#94a3b8', label: '—' }
        };
        const style = map[result] || { bg: '#eee', color: '#999', label: result };
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: '0.78rem',
                fontWeight: 700,
                background: style.bg,
                color: style.color
            }}>
                {style.label}
            </span>
        );
    }

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '860px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}

                    <header style={{ marginBottom: '28px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <h1 style={{ fontSize: '2rem', marginBottom: '6px' }}>Inspection Summary</h1>
                                {clientName && <p style={{ color: 'var(--dash-muted)', margin: '0 0 2px' }}>Client: {clientName}</p>}
                                {inspection.siteAddress && <p style={{ color: 'var(--dash-muted)', margin: '0 0 2px' }}>Site: {inspection.siteAddress}</p>}
                                {inspection.technicianId?.name && <p style={{ color: 'var(--dash-muted)', margin: 0 }}>Technician: {inspection.technicianId.name}</p>}
                            </div>
                            {inspection.createdAt && (
                                <span style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}>
                                    {new Date(inspection.createdAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </header>

                    {inspection.isNewBuilding ?
                        (
                            <div className="banner" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', marginBottom: 18 }}>
                                Initial inspection · {typeof inspection.devicesRegisteredDuringVisit === 'number' ?
                                    `${inspection.devicesRegisteredDuringVisit} device(s) registered while on site`
                                : 'Device registry captured during this visit'}
                            </div>
                        )
                    : null}

                    {/* Score summary */}
                    <div className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 900, color: '#2e8a57' }}>{passed}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--dash-muted)', fontWeight: 600 }}>Passed</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 900, color: '#c0392b' }}>{failed}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--dash-muted)', fontWeight: 600 }}>Failed</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 900, color: '#b8860b' }}>{deficient}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--dash-muted)', fontWeight: 600 }}>Deficient</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--dash-muted)' }}>{inspection.items.length}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--dash-muted)', fontWeight: 600 }}>Total</div>
                            </div>
                        </div>
                    </div>

                    {inspection.deviceDriven ?
                        (
                            <div className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                                <div className="quote-section-title" style={{ marginBottom: '14px' }}>Device-by-device results</div>
                                <p style={{ color: 'var(--dash-muted)', fontSize: '0.88rem', marginTop: 0 }}>
                                    Expand each asset to review individual test lines captured on this inspection.
                                </p>
                                {deviceCategories.map((cat) => {
                                    const lines = inspection.items.filter((i) => i.category === cat);
                                    const anyFail = lines.some((l) => l.result === 'fail' || l.result === 'deficient');
                                    return (
                                        <details key={cat} style={{ marginBottom: 12, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: '10px 12px' }}>
                                            <summary style={{ fontWeight: 800, cursor: 'pointer', listStyle: 'none' as const }}>
                                                {cat}{' '}
                                                <span style={{ color: anyFail ? '#c0392b' : '#2e8a57', fontSize: '0.78rem', marginLeft: 8 }}>
                                                    {anyFail ? 'Needs attention' : 'All clear / N/A'}
                                                </span>
                                            </summary>
                                            <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                                                {lines.map((line) => (
                                                    <div
                                                        key={line.itemId}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            gap: 10,
                                                            fontSize: '0.88rem',
                                                            borderTop: '1px solid rgba(0,0,0,0.05)',
                                                            paddingTop: 8
                                                        }}
                                                    >
                                                        <span>{line.label}</span>
                                                        {resultBadge(String(line.result || '—'))}
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    );
                                })}
                            </div>
                        )
                    : null}

                    {trackedDeficiencies.length > 0 && (
                        <div className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                            <div className="quote-section-title" style={{ marginBottom: '14px' }}>
                                Formal deficiency records
                            </div>
                            <div style={{ display: 'grid', gap: '14px' }}>
                                {trackedDeficiencies.map((d) => (
                                    <div
                                        key={d._id}
                                        style={{
                                            display: 'flex',
                                            gap: '14px',
                                            alignItems: 'flex-start',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.06)'
                                        }}
                                    >
                                        {d.photos?.[0] ? (
                                            <a href={buildApiUrl(d.photos[0])} target="_blank" rel="noopener noreferrer">
                                                <img
                                                    src={buildApiUrl(d.photos[0])}
                                                    alt=""
                                                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                                />
                                            </a>
                                        ) : (
                                            <div style={{ width: 72, height: 72, borderRadius: 8, background: 'rgba(0,0,0,0.06)' }} />
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 800 }}>{d.description}</div>
                                            <div style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                <span
                                                    style={{
                                                        fontSize: '0.72rem',
                                                        fontWeight: 800,
                                                        padding: '2px 8px',
                                                        borderRadius: 999,
                                                        background:
                                                            d.severity === 'critical'
                                                                ? 'rgba(192,57,43,0.12)'
                                                                : d.severity === 'major'
                                                                  ? 'rgba(230,168,23,0.15)'
                                                                  : 'rgba(59,130,246,0.12)',
                                                        color:
                                                            d.severity === 'critical'
                                                                ? '#c0392b'
                                                                : d.severity === 'major'
                                                                  ? '#b8860b'
                                                                  : '#2563eb'
                                                    }}
                                                >
                                                    {d.severity}
                                                </span>
                                                <span style={{ fontSize: '0.82rem', color: 'var(--dash-muted)' }}>{d.status}</span>
                                            </div>
                                        </div>
                                        {canManage ? (
                                            <div>
                                                {!d.linkedQuoteId ? (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                                                        disabled={defQuoteBusy === d._id}
                                                        onClick={() => createQuoteFromDeficiency(d._id)}
                                                    >
                                                        {defQuoteBusy === d._id ? '…' : 'Create quote'}
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: '0.78rem', color: 'var(--dash-muted)' }}>Quote linked</span>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                        <div className="quote-section-title" style={{ marginBottom: '14px' }}>Checklist items (fail / deficient)</div>
                        {deficiencies.length === 0 ? <p style={{ margin: 0, color: 'var(--dash-muted)' }}>No failed or deficient checklist rows.</p> : deficiencies.map((item) => (
                            <div key={item.itemId} style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                <div style={{ fontWeight: 700 }}>{item.label}</div>
                                <div style={{ color: 'var(--dash-muted)', fontSize: '0.85rem' }}>{item.category} {item.testType ? `• ${item.testType}` : ''}</div>
                                {item.note ? <div style={{ marginTop: '4px' }}>{item.note}</div> : null}
                            </div>
                        ))}
                    </div>

                    {categories.map((cat) => {
                        const catItems = inspection.items.filter((i) => i.category === cat);
                        return (
                            <div key={cat} className="quote-card" style={{ marginBottom: '16px', padding: '20px 24px' }}>
                                <div className="quote-section-title" style={{ marginBottom: '14px' }}>{cat}</div>
                                {catItems.map((item) => (
                                    <div
                                        key={item.itemId}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            padding: '10px 0',
                                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                                            background:
                                                item.result === 'pass' ? 'rgba(46,138,87,0.08)'
                                                    : item.result === 'fail' ? 'rgba(192,57,43,0.08)'
                                                        : item.result === 'deficient' ? 'rgba(230,168,23,0.08)'
                                                            : 'rgba(120,120,120,0.08)',
                                            borderRadius: '6px',
                                            marginBottom: '4px'
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{item.label}</span>
                                            {item.result === 'deficient' && item.note && (
                                                <p style={{ margin: '4px 0 0', color: '#b8860b', fontSize: '0.85rem' }}>
                                                    {item.note}
                                                </p>
                                            )}
                                            {item.photoUrl && (
                                                <a
                                                    href={item.photoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ fontSize: '0.8rem', color: 'var(--dash-muted)' }}
                                                >
                                                    View photo
                                                </a>
                                            )}
                                        </div>
                                        <div style={{ marginLeft: '16px' }}>
                                            {resultBadge(item.result)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate(listPath)}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Inspections
                        </button>

                        {canManage && deficient > 0 && (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={generateQuote}
                                disabled={generating}
                                style={{
                                    padding: '12px 28px',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 14px rgba(46,138,87,0.3)'
                                }}
                            >
                                {generating ? 'Generating…' : `Generate Quote from ${deficient} Deficiencie${deficient !== 1 ? 's' : ''}`}
                            </button>
                        )}

                        {inspection.status === 'completed' ? (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => downloadInspectionPdf()}
                                disabled={pdfBusy}
                                style={{ padding: '12px 22px', fontSize: '0.96rem' }}
                            >
                                {pdfBusy ? 'Preparing PDF…' : 'Download PDF report'}
                            </button>
                        ) : null}

                        {canManage && (
                            <button type="button" className="dash-btn dash-btn-ghost" onClick={downloadReport} disabled={downloading}>
                                {downloading ? 'Downloading…' : 'Download JSON'}
                            </button>
                        )}

                        {canManage && deficient === 0 && (
                            <p style={{ color: 'var(--dash-muted)', fontSize: '0.88rem', alignSelf: 'center' }}>
                                No deficiencies found — no quote needed.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
